import { useEffect, useRef } from "react";
import type { Lang } from "../types.ts";
import { t } from "../i18n/ui.ts";
import { SHORTCUTS } from "../state/useKeyboard.ts";

interface Props {
  open: boolean;
  lang: Lang;
  onClose: () => void;
}

/** Keyboard reference. Uses <dialog> so focus trapping comes from the platform. */
export function ShortcutsDialog({ open, lang, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dialog"
      onClose={onClose}
      onKeyDown={(e) => {
        // Close on Escape ourselves rather than relying on the platform's
        // default action for <dialog>, which a stray preventDefault upstream
        // can cancel. stopPropagation keeps the same Escape from also
        // reaching the page's shortcuts and exiting the view behind us.
        // No preventDefault, so the native close still runs where it works;
        // onClose is idempotent.
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
      aria-labelledby="sc-title"
    >
      <h2 id="sc-title">{t(lang, "shortcutsTitle")}</h2>
      <dl className="shortcuts">
        {SHORTCUTS.map((s) => (
          <div className="shortcuts__row" key={s.label}>
            <dt>
              {s.keys.map((k) => (
                <kbd key={k}>{k}</kbd>
              ))}
            </dt>
            <dd>{t(lang, s.label, s.arg ? { n: s.arg } : undefined)}</dd>
          </div>
        ))}
      </dl>
      <form method="dialog">
        <button type="submit" className="btn">{t(lang, "close")}</button>
      </form>
    </dialog>
  );
}
